import { Link } from 'react-router-dom';
import type { Comic } from '@/mocks/comics';

interface ComicCardProps {
  comic: Comic;
  variant?: 'default' | 'compact' | 'featured';
}

export default function ComicCard({ comic, variant = 'default' }: ComicCardProps) {
  const statusColor = comic.status === '完结' ? 'bg-secondary-500 text-background-50' : 'bg-primary-500 text-background-50';
  const ratingStars = Array.from({ length: 5 }, (_, i) => (
    <i
      key={i}
      className={`text-xs ${i < Math.floor(comic.rating) ? 'ri-star-fill text-accent-500' : i < comic.rating ? 'ri-star-half-line text-accent-500' : 'ri-star-line text-foreground-300'}`}
    ></i>
  ));

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${comic.id}`}
        className="flex gap-3 p-3 rounded-lg hover:bg-background-100 transition-all duration-200 group"
      >
        <div className="w-16 h-20 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={comic.coverImage}
            alt={comic.title}
            className="w-full h-full object-cover object-top"
            title={comic.title}
          />
        </div>
        <div className="flex flex-col justify-center min-w-0">
          <h4 className="text-sm font-semibold text-foreground-950 truncate group-hover:text-primary-500 transition-colors duration-200">
            {comic.title}
          </h4>
          <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
          <div className="flex items-center gap-1 mt-1">
            {ratingStars}
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${comic.id}`}
        className="flex flex-col sm:flex-row gap-4 p-4 rounded-xl bg-background-100 hover:bg-background-200 transition-all duration-300 group"
      >
        <div className="w-full sm:w-32 h-44 sm:h-40 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={comic.coverImage}
            alt={comic.title}
            className="w-full h-full object-cover object-top"
            title={comic.title}
          />
        </div>
        <div className="flex flex-col justify-center min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-bold text-foreground-950 group-hover:text-primary-500 transition-colors duration-200">
              {comic.title}
            </h3>
            <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${statusColor}`}>
              {comic.status}
            </span>
          </div>
          <p className="text-sm text-foreground-500 mt-1">{comic.author}</p>
          <div className="flex items-center gap-1 mt-2">
            {ratingStars}
            <span className="text-sm text-foreground-600 ml-1">{comic.rating}</span>
          </div>
          <p className="text-sm text-foreground-600 mt-2 line-clamp-2 leading-relaxed">{comic.description}</p>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {comic.tags.map((tag) => (
              <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${comic.id}`}
      className="flex flex-col group rounded-xl overflow-hidden bg-background-50 hover:bg-background-100 transition-all duration-300"
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={comic.coverImage}
          alt={comic.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          title={comic.title}
        />
        <div className="absolute top-2 left-2">
          <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${statusColor}`}>
            {comic.status}
          </span>
        </div>
        <div className="absolute top-2 right-2 flex items-center gap-1 bg-foreground-950/70 text-background-50 text-xs px-2 py-0.5 rounded-full">
          <i className="ri-star-fill text-xs"></i>
          {comic.rating}
        </div>
      </div>
      <div className="p-3 flex flex-col gap-1">
        <h3 className="text-sm font-semibold text-foreground-950 truncate group-hover:text-primary-500 transition-colors duration-200">
          {comic.title}
        </h3>
        <p className="text-xs text-foreground-500">{comic.author}</p>
        <div className="flex flex-wrap gap-1 mt-1">
          {comic.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}